import { type StudyRunCaseDashboardAdvisoryGapDto, StudyRunCaseDashboardAdvisoryGapSeverity } from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { formatEnumLabel } from '@utils/meia-enum.util';
import { type FC } from 'react';
import { InfoTooltip } from './meia-p4-dashboard-primitives.component';

type AdvisoryGapsPanelProps = {
  gaps: StudyRunCaseDashboardAdvisoryGapDto[];
};

export const AdvisoryGapsPanel: FC<AdvisoryGapsPanelProps> = (props) => {
  const { gaps } = props;

  return (
    <Tile
      contentClassName="gap-3"
      header={{
        title: (
          <span className="inline-flex items-center gap-1.5">
            Advisory Gaps
            <InfoTooltip>
              Advisory gaps are not blocking validation errors. They flag places where authored traceability or
              technology snapshots may make a run case harder to trust.
            </InfoTooltip>
          </span>
        ),
      }}
    >
      <div className="flex max-h-[360px] flex-col overflow-y-auto rounded-xl border border-gray-200">
        {gaps.length === 0 ? (
          <div className="p-3 text-xs text-gray-500">No advisory gaps for this thread.</div>
        ) : (
          gaps.map((gap) => (
            <div
              className="border-b border-gray-100 p-3 last:border-b-0"
              key={gap.id}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 text-sm font-medium text-gray-950">{gap.summary}</div>
                <span className="flex shrink-0 items-center gap-1.5">
                  <Badge
                    size="xs"
                    variant="default"
                  >
                    {formatEnumLabel(gap.kind)}
                  </Badge>
                  <Badge
                    size="xs"
                    variant={advisoryGapVariant(gap.severity)}
                  >
                    {advisoryGapSeverityLabel(gap.severity)}
                  </Badge>
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600">{gap.detail}</div>
              <div className="mt-2 font-mono text-[10px] text-gray-400">{gap.sourceReferences.join(' · ')}</div>
            </div>
          ))
        )}
      </div>
      <p className="text-[11px] text-gray-400">
        Advisory only — these never block a run. Blocking findings surface through validation results.
      </p>
    </Tile>
  );
};

const advisoryGapSeverityLabel = (severity: StudyRunCaseDashboardAdvisoryGapSeverity): string => {
  switch (severity) {
    case StudyRunCaseDashboardAdvisoryGapSeverity.HIGH:
      return 'High';
    case StudyRunCaseDashboardAdvisoryGapSeverity.LOW:
      return 'Low';
    case StudyRunCaseDashboardAdvisoryGapSeverity.MEDIUM:
      return 'Medium';
  }
};

const advisoryGapVariant = (severity: StudyRunCaseDashboardAdvisoryGapSeverity): BadgeProps['variant'] => {
  switch (severity) {
    case StudyRunCaseDashboardAdvisoryGapSeverity.HIGH:
      return 'destructive';
    case StudyRunCaseDashboardAdvisoryGapSeverity.LOW:
      return 'pending';
    case StudyRunCaseDashboardAdvisoryGapSeverity.MEDIUM:
      return 'warning';
  }
};
